import { useRequest } from 'features/request/useRequest';
import type { WorkOrderPriceItemFilterInput } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { uerWorkOrderPriceItemsGql } from '../../workOrderQueries';
import { getWorkOrderTableKey, getWorkOrderVariables, getWorkOrderWhereFromFilter } from '../helpers';

type TWorkOrderData = {
    workOrderId: number;
    objectId: number | undefined;
    subObjectId: number | undefined;
    constructionTypeId: number;
    gridTableRef?: AgGridReact<any> | null;
};

export const useWorkOrderData = (props: TWorkOrderData) => {
    const { workOrderId, objectId, subObjectId, constructionTypeId, gridTableRef } = props;
    const { state }: { state: { actNavInfo?: any } } = useLocation();
    const tableKey = useMemo(
        () =>
            getWorkOrderTableKey({
                workOrderId,
                constructionTypeId,
                objectId,
                subObjectId,
            }),

        [constructionTypeId, objectId, subObjectId, workOrderId],
    );
    const [filtersWhere, setFiltersWhere] = useState<WorkOrderPriceItemFilterInput | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevState) => ({
            ...prevState,
            ...getWorkOrderWhereFromFilter(fieldName, checkedValues),
        }));
    }, []);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    tableKey,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                    Boolean(state?.actNavInfo?.actId),
                ),
            }),
            [changeFilterHandler, state?.actNavInfo?.actId, tableKey],
        ),
    );

    const [data, , , request] = useRequest({
        query: uerWorkOrderPriceItemsGql,
        withEffect: false,
        requestId: 'workOrderData',
    });

    const workOrderDataVariables = useMemo<
        | {
              workOrderId: number;
              constructionTypeId: number;
              where?: WorkOrderPriceItemFilterInput;
          }
        | undefined
    >(() => {
        if (constructionTypeId && workOrderId) {
            return {
                workOrderId,
                constructionTypeId,
                ...getWorkOrderVariables(objectId, subObjectId, filtersWhere),
            };
        }

        return undefined;
    }, [constructionTypeId, filtersWhere, objectId, subObjectId, workOrderId]);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderData',
        requestFn: request,
        requestVariables: workOrderDataVariables,
    });

    useEffect(() => {
        if (data?.uerWorkOrderPriceItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                data.uerWorkOrderPriceItems.items,
                data.uerWorkOrderPriceItems?.totalCount,
            );
        }
    }, [
        data?.uerWorkOrderPriceItems?.items,
        data?.uerWorkOrderPriceItems?.totalCount,
        getRowParams,
        gridTableRef?.api,
    ]);

    const totalItems = useMemo(
        () => data?.uerWorkOrderPriceItems?.totalCount,
        [data?.uerWorkOrderPriceItems?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    return {
        workOrderPriceData: dataSource,
        fetchedItems,
        totalItems,
        changeFilterHandler,
    };
};
